import { ActionTree, Commit } from 'vuex';
import { ConsumerApplicationState, ConsumerApplication, ConsumerApplicationStatus, AccountConversion } from './types';
import { RootState } from '../types';
import { SET_CONSUMER_RESPONSE, SET_CONSUMER_RESPONSE_ERRORS, SET_CONSUMER_APPLICATION_STATUS, SET_ACCOUNT_CONVERSION_STATUS } from '../mutation-types';
import { postConsumer, getApplicationStatus, postAccountConversion } from '@/api/consumer-application.api';

const getIncome = (consumerApplication: ConsumerApplication): string => {
    let income = consumerApplication!.employmentAndIncome!.totalAnnualIncome;
    income = income!.replace('$', '');
    income = income!.split(',').join('');
    return income;
}

const convertStringSubmitResultToStatus = (statusString: string): ConsumerApplicationStatus => {
    // these expected statusString values must map 1-to-1 to ApplicationSubmissionStatus enum in kcc-consumer-application service
    switch (statusString) {
        case ('APPROVED'): {
            return ConsumerApplicationStatus.APPROVED;
        }
        case ('IN_REVIEW'): {
            return ConsumerApplicationStatus.IN_REVIEW;
        }
        case ('NOT_ELIGIBLE'): {
            return ConsumerApplicationStatus.NOT_ELIGIBLE;
        }
        case ('DECLINED'): {
            return ConsumerApplicationStatus.DECLINED;
        }
    }

    return ConsumerApplicationStatus.ERROR;
}

const DEFAULT_POLLING_INTERVAL_MS = 15000; // 15 seconds == 15000 milliseconds
const DEFAULT_NUMBER_OF_RETRIES = 12; // total of 180 seconds

let checkStatusTimerId: number;
let retryNumber = 0;

const checkApplicationStatus = (reservationCode: string, commit: Commit): void => {
    getApplicationStatus(reservationCode)
        .then((response: any) => {
            retryNumber++;
            if ('IN_PROGRESS' != response.data.data.applicationSubmissionStatus) {
                
                const applicationStatus: ConsumerApplicationStatus = convertStringSubmitResultToStatus(response.data.data.applicationSubmissionStatus);
                commit(SET_CONSUMER_APPLICATION_STATUS, applicationStatus);

                if(ConsumerApplicationStatus.APPROVED == applicationStatus){
                    commit(SET_CONSUMER_RESPONSE, response.data.data);
                }
                
                clearInterval(checkStatusTimerId);
            } else {
                if (retryNumber >= DEFAULT_NUMBER_OF_RETRIES) {
                    commit(SET_CONSUMER_RESPONSE_ERRORS, 'The application submission timed out.');
                    commit(SET_CONSUMER_APPLICATION_STATUS, ConsumerApplicationStatus.ERROR);
                    clearInterval(checkStatusTimerId);
                }
            }
        })
        .catch(error => {
            commit(SET_CONSUMER_RESPONSE_ERRORS, error);
            commit(SET_CONSUMER_APPLICATION_STATUS, ConsumerApplicationStatus.ERROR);
            clearInterval(checkStatusTimerId);
        });
}

export const actions: ActionTree<ConsumerApplicationState, RootState> = {
    async postConsumerApplication({ commit, state }) {

        const consumerApplication: ConsumerApplication = state.consumerApplication || {};
        consumerApplication!.employmentAndIncome!.totalAnnualIncome = getIncome(consumerApplication);
        commit(SET_CONSUMER_APPLICATION_STATUS, ConsumerApplicationStatus.PROCESSING);
        return postConsumer(consumerApplication)
            .then((response) => {
                if ('IN_PROGRESS' == response.data.data.applicationSubmissionStatus) {
                    checkStatusTimerId = setInterval(
                        () =>
                            checkApplicationStatus(
                                consumerApplication!.candidate!.reservationCode!,
                                commit
                            ),
                        DEFAULT_POLLING_INTERVAL_MS
                    );
                } else {
                    commit(SET_CONSUMER_RESPONSE, response.data.data);

                    const applicationStatus: ConsumerApplicationStatus = convertStringSubmitResultToStatus(response.data.data.applicationSubmissionStatus);
                    commit(SET_CONSUMER_APPLICATION_STATUS, applicationStatus);
                }
            }, (error) => {
                commit(SET_CONSUMER_RESPONSE_ERRORS, error);
                commit(SET_CONSUMER_APPLICATION_STATUS, ConsumerApplicationStatus.ERROR);
            });

    },

    async updateAccountConversionStatus({ commit, getters, rootGetters }, reservationCode) {
        const accountStatus: boolean = getters['accountConversionConsent'];
        const fiId: number = rootGetters['financialInstitution/fiId'];
        const payload: AccountConversion = {
            reservationCode: reservationCode,
            consent: accountStatus,
            fiId: fiId,
        };
        return postAccountConversion(payload)
            .then((response) => {
                if (response.data.data == true) {
                    commit('SET_CONSUMER_APPLICATION_STATUS', ConsumerApplicationStatus.UPGRADED);
                } else if (accountStatus == false) {
                    commit('SET_CONSUMER_APPLICATION_STATUS', ConsumerApplicationStatus.UPGRADE_DECLINED);
                }

                commit(SET_ACCOUNT_CONVERSION_STATUS, response.data.data);
            })
            .catch(error => {
                commit(SET_CONSUMER_RESPONSE_ERRORS, error);
                commit(SET_CONSUMER_APPLICATION_STATUS, ConsumerApplicationStatus.ERROR);
            });
    }
};

export default actions;
